import { z } from 'zod';
import { ACTIVITY_TYPES, ENTITY_STATUSES, ENTRY_SOURCES } from '../enums.js';
import { isoDateSchema, uuidSchema } from './common.js';
export const MAX_ENTRY_RANGE_DAYS = 62;
// ---------------------------------------------------------------- options
export const projectOptionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    code: z.string().nullable(),
    clientName: z.string().nullable(),
    color: z.string(),
    status: z.enum(ENTITY_STATUSES),
});
export const activityOptionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    type: z.enum(ACTIVITY_TYPES),
    status: z.enum(ENTITY_STATUSES),
    /**
     * The project this activity belongs to (design §7.3). The client narrows the activity picker to
     * the selected project with it. NULL means the activity is assigned to no project and so can be
     * booked to nowhere — a leftover of the 0005 split for an admin to place or archive.
     */
    projectId: uuidSchema.nullable(),
});
export const optionsResponseSchema = z.object({
    projects: z.array(projectOptionSchema),
    activities: z.array(activityOptionSchema),
    /** Most recently used combinations, newest first. */
    recent: z.array(z.object({ projectId: uuidSchema, activityId: uuidSchema, lastUsedAt: z.string() })),
});
// ---------------------------------------------------------------- time entries
const descriptionSchema = z.string().trim().max(2000).nullable().optional();
/**
 * What a write to a time entry may say. There is no `startedAt`/`endedAt` here: time is reported
 * as hours on a day, so a duration is the whole of it. Entries written before that decision keep
 * their stored clock times — `timeEntrySchema` still reads them, and exports still carry them —
 * but no request can set them, and writing a duration clears the pair rather than leave times
 * that no longer add up to it.
 */
const timeEntryBase = z.object({
    userId: uuidSchema.optional(),
    projectId: uuidSchema,
    activityId: uuidSchema,
    entryDate: isoDateSchema,
    durationMinutes: z.number().int().min(1).max(1440),
    description: descriptionSchema,
});
export const createTimeEntrySchema = timeEntryBase;
export const updateTimeEntrySchema = timeEntryBase.omit({ userId: true }).partial();
/**
 * Only the week grid warns, and only about what the cell was hiding: several entries behind it
 * became one total (`ENTRIES_MERGED`), or several were deleted together when it was cleared
 * (`ENTRIES_DELETED`). Both carry the ids they affected in `entryIds`. Entry writes and copy-week
 * have nothing to report any more — the overlap and crossing-midnight warnings went with the
 * clock times they described — so neither response carries this at all.
 */
export const timeEntryWarningSchema = z.object({
    code: z.enum(['ENTRIES_MERGED', 'ENTRIES_DELETED']),
    message: z.string(),
    entryIds: z.array(uuidSchema).optional(),
});
export const timeEntrySchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    project: projectOptionSchema,
    activity: activityOptionSchema,
    entryDate: isoDateSchema,
    durationMinutes: z.number().int(),
    /** Read-only history: entries written before hours-per-day still carry the pair. */
    startedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    description: z.string().nullable(),
    source: z.enum(ENTRY_SOURCES),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.object({ id: uuidSchema, name: z.string() }),
});
export const listTimeEntriesQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    userId: uuidSchema.optional(),
});
export const timeEntryListSchema = z.object({
    data: z.array(timeEntrySchema),
    totals: z.object({ byDate: z.record(z.string(), z.number().int()), total: z.number().int() }),
});
export const timeEntryMutationResponseSchema = z.object({
    entry: timeEntrySchema,
});
// ---------------------------------------------------------------- timesheet grid
export const weekQuerySchema = z.object({
    date: isoDateSchema,
    userId: uuidSchema.optional(),
    /** `projectId:activityId` pairs to keep as (possibly empty) rows. */
    keepRows: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((v) => (v === undefined ? [] : Array.isArray(v) ? v : v.split(',')))
        // Bounded deliberately. The grid asks for at most the ten most recently used pairs; an
        // unbounded list would be a free way to make one request do arbitrary work, and a pair that
        // is not two uuids cannot match anything anyway.
        .pipe(z.array(z.string().max(100)).max(50)),
});
export const weekCellSchema = z.object({
    minutes: z.number().int(),
    /**
     * Every entry behind this total, oldest first. Normally one: the cell is the day's total for a
     * project and activity, and writing it leaves a single entry. Several means history from before
     * the grid owned that total, and the next edit of this cell collapses them into one.
     */
    entryIds: z.array(uuidSchema),
});
export const weekRowSchema = z.object({
    project: projectOptionSchema,
    activity: activityOptionSchema,
    cells: z.record(z.string(), weekCellSchema),
    rowTotal: z.number().int(),
});
export const weekResponseSchema = z.object({
    weekStart: isoDateSchema,
    weekEnd: isoDateSchema,
    days: z.array(isoDateSchema),
    rows: z.array(weekRowSchema),
    dayTotals: z.record(z.string(), z.number().int()),
    weekTotal: z.number().int(),
});
export const putCellSchema = z.object({
    userId: uuidSchema.optional(),
    projectId: uuidSchema,
    activityId: uuidSchema,
    entryDate: isoDateSchema,
    durationMinutes: z.number().int().min(0).max(1440),
});
export const putCellResponseSchema = z.object({
    entry: timeEntrySchema.nullable(),
    rowTotal: z.number().int(),
    dayTotal: z.number().int(),
    weekTotal: z.number().int(),
    warnings: z.array(timeEntryWarningSchema),
});
export const monthQuerySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    userId: uuidSchema.optional(),
});
export const monthResponseSchema = z.object({
    month: z.string(),
    days: z.array(z.object({ date: isoDateSchema, minutes: z.number().int(), entryCount: z.number().int() })),
    monthTotal: z.number().int(),
});
// ---------------------------------------------------------------- personal activities
export const createPersonalActivitySchema = z.object({
    /** An activity belongs to one project, so creating one means saying which. */
    projectId: uuidSchema,
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).nullable().optional(),
});
export const updatePersonalActivitySchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500).nullable(),
    status: z.enum(ENTITY_STATUSES),
})
    .partial();
export const personalActivitySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    type: z.enum(ACTIVITY_TYPES),
    status: z.enum(ENTITY_STATUSES),
    /** Null only for an activity the 0005 split could not place; see `activityOptionSchema`. */
    project: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    entryCount: z.number().int(),
    createdAt: z.string(),
});
// ---------------------------------------------------------------- dashboards & reporting filters
export const reportFiltersSchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    userIds: z.array(uuidSchema).optional(),
    groupIds: z.array(uuidSchema).optional(),
    projectIds: z.array(uuidSchema).optional(),
    activityIds: z.array(uuidSchema).optional(),
});
const minutesBy = (shape) => z.array(z.object({ ...shape, minutes: z.number().int() }));
export const aggregationResultSchema = z.object({
    range: z.object({ from: isoDateSchema, to: isoDateSchema }),
    totalMinutes: z.number().int(),
    entryCount: z.number().int(),
    activeUserCount: z.number().int(),
    byUser: minutesBy({ userId: uuidSchema, name: z.string(), email: z.string() }),
    byProject: minutesBy({ projectId: uuidSchema, name: z.string(), color: z.string() }),
    byActivity: minutesBy({ activityId: uuidSchema, name: z.string() }),
    byGroup: minutesBy({ groupId: uuidSchema, name: z.string() }),
    byDay: minutesBy({ date: isoDateSchema }),
    byCombination: minutesBy({ projectId: uuidSchema, activityId: uuidSchema }),
    /**
     * Per-person-per-combination minutes: one row per user × project × activity, which on a real
     * organization is thousands of rows and the bulk of the response body. Nothing renders it by
     * default, so it is only produced when a caller asks (`?detail=user-project-activity`).
     */
    byUserProjectActivity: minutesBy({
        userId: uuidSchema,
        projectId: uuidSchema,
        activityId: uuidSchema,
    }).optional(),
});
/** Comma-separated uuid list in a query string, as the org dashboard's URL filters send it. */
const uuidListSchema = z
    .union([z.string(), z.array(uuidSchema)])
    .optional()
    .transform((v) => {
    if (v === undefined)
        return undefined;
    const list = Array.isArray(v) ? v : v.split(',').filter(Boolean);
    return list.length ? list : undefined;
})
    .pipe(z.array(uuidSchema).max(200).optional());
/**
 * Organization dashboard filters (FR8). Every one is reflected in the URL, so the query schema
 * has to accept what a link carries: repeated params or a comma-separated list.
 */
export const orgDashboardQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    /** Opt-in for the heavy per-person-per-combination breakdown. */
    detail: z.enum(['user-project-activity']).optional(),
    userIds: uuidListSchema,
    groupIds: uuidListSchema,
    projectIds: uuidListSchema,
    activityIds: uuidListSchema,
});
export const orgDashboardSchema = aggregationResultSchema.extend({
    /** Users in scope who logged nothing in the range — the "missing timesheets" tab. */
    usersWithoutTime: z.array(z.object({ userId: uuidSchema, name: z.string(), email: z.string() })),
    previous: z.object({
        range: z.object({ from: isoDateSchema, to: isoDateSchema }),
        totalMinutes: z.number().int(),
        entryCount: z.number().int(),
    }),
});
export const dashboardQuerySchema = z.object({
    period: z.enum(['day', 'week', 'month']).default('week'),
    date: isoDateSchema.optional(),
});
export const dashboardSummarySchema = aggregationResultSchema.extend({
    period: z.enum(['day', 'week', 'month']),
    previous: z.object({
        range: z.object({ from: isoDateSchema, to: isoDateSchema }),
        totalMinutes: z.number().int(),
        entryCount: z.number().int(),
    }),
    activeDays: z.number().int(),
});
// ---------------------------------------------------------------- copy week
export const COPY_WEEK_MODES = ['ADD', 'REPLACE'];
export const COPY_SKIP_REASONS = ['NOT_SELECTABLE', 'ARCHIVED', 'DAY_LIMIT'];
export const copyWeekSchema = z.object({
    /** Whose timesheet to copy; omitted or own id for self, another id requires ADMIN. */
    userId: uuidSchema.optional(),
    /** Any date inside the week to copy from. */
    sourceDate: isoDateSchema,
    /** Any date inside the week to copy into. */
    targetDate: isoDateSchema,
    /** ADD appends the copies; REPLACE first deletes the target week's entries. */
    mode: z.enum(COPY_WEEK_MODES).default('ADD'),
    includeDescriptions: z.boolean().default(true),
    /** Validate and report what would happen without writing anything. */
    preview: z.boolean().default(false),
});
export const copySkippedEntrySchema = z.object({
    entryDate: isoDateSchema,
    projectName: z.string(),
    activityName: z.string(),
    minutes: z.number().int(),
    reason: z.enum(COPY_SKIP_REASONS),
});
export const copyWeekResultSchema = z.object({
    sourceWeekStart: isoDateSchema,
    targetWeekStart: isoDateSchema,
    mode: z.enum(COPY_WEEK_MODES),
    /** true when nothing was written (dry run). */
    preview: z.boolean(),
    sourceEntryCount: z.number().int(),
    sourceMinutes: z.number().int(),
    copiedCount: z.number().int(),
    copiedMinutes: z.number().int(),
    deletedCount: z.number().int(),
    deletedMinutes: z.number().int(),
    skipped: z.array(copySkippedEntrySchema),
    targetMinutesBefore: z.number().int(),
    targetMinutesAfter: z.number().int(),
});
//# sourceMappingURL=time.js.map